<template>
  <section :class="$style.description">
    <h2>{{ t('details.DESCRIPTION') }}</h2>
    <div
      v-if="!isContainingContactInformation"
      :class="$style.descriptionBody"
      v-html="listingDescription"
    />
    <div v-else :class="$style.descriptionBody">
      <MaskedDescription
        :description="listingDescription"
        @maskedContentClicked="emit('maskedContentClicked')"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

import MaskedDescription from '@/components/TenantPlus/MaskedDescription.vue';
import { useListingLocalizedContent } from '@/composables/useListingLocalizedContent';
import { sanitizeHtml } from '@/helpers/Sanitize/Sanitize';
import { removeLinksFromDescription } from '@/helpers/TextFormatting/TextFormatting';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { listingDescription: resolvedDescription } = useListingLocalizedContent();

const LINK_EXCEPTIONS = ['mywincasa.ch'];

const emit = defineEmits<{
  maskedContentClicked: [];
}>();

const listingDescription = computed((): string => {
  const rawDescription = resolvedDescription.value ?? '';

  // Sanitise once here so both the v-html path and the MaskedDescription path
  // below receive already-clean HTML (defense-in-depth over listing-api).
  return sanitizeHtml(removeLinksFromDescription(rawDescription, LINK_EXCEPTIONS));
});
const isContainingContactInformation = computed((): boolean => {
  return listingDescription.value.includes('<span class="tenantPlusExclusive">');
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

.description {
  h2 {
    @include heading-2;
    margin: 0 0 $space-100;
  }
}

.descriptionBody {
  @include body-2;

  hyphens: auto;
  word-break: break-word;

  // v-html descriptions are free-form (p, br, div, lists, headings, …).
  // Container-level body-2 does not reliably override user-agent styles on nested tags.
  p,
  li,
  div,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: var(--typo-body-2-font-size);
    line-height: var(--typo-body-2-line-height);
  }

  p,
  div,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    @include font-bold;
  }

  // ~16px between block siblings (TipTap / ProseMirror top-level rhythm).
  :is(p, div, ul, ol, h1, h2, h3, h4, h5, h6) + :is(p, div, ul, ol, h1, h2, h3, h4, h5, h6) {
    margin-top: $space-100;
  }

  br + p,
  br + div,
  br + ul,
  br + ol,
  strong + ul,
  strong + ol {
    margin-top: $space-100;
  }

  ul,
  ol {
    margin-top: 0;
    margin-bottom: $space-100;
    // Explicit indent — do not rely on browser defaults.
    padding-inline-start: $space-250;
    list-style-position: outside;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  li {
    margin: 0;
  }

  li + li {
    margin-top: $space-25;
  }

  li p {
    display: inline;
    margin: 0;
  }

  br {
    display: block;
    margin-top: $space-25;
    content: '';
  }

  br:first-child {
    margin-top: 0;
  }
}
</style>
